import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { ServicesDialog } from 'src/components/Dialogs/ServicesDialog/ServicesDialog';
import { Modal } from 'src/components/Modal/ServicesModal/Modal';
import { RightServicesDrawer } from 'src/components/RightDrawer/RightServicesDrawer';
import { StudioCategoriesTable } from 'src/components/StudioCategoriesTable';
import { ServicesSearchTypes, StudiosSearchTypes } from 'src/components/ToggleBox/ToggleBox.types';
import { UserRoles } from 'src/constants/user';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { CustomActionCardMenuActionType, } from 'src/screens/services/components/CustomActionCardMenu/CustomActionCardMenu.types';
import { setSelectedStudio } from 'src/store/reducers/currentStudioSlice';
import { resetSelectedStudio } from 'src/store/reducers/selectedStudioSlice';
import { getStudios, updateSearchParams, } from 'src/store/reducers/studiosSlice';
import { filterCategoriesData, sortCategoriesData} from 'src/utils/helpers';
import { getSearchParams } from 'src/utils/search';

import { EmptyPageState, MainPage, SubmitButton, Table, ToggleBox } from '../../components';
import { StudioDialog } from '../../components/Dialogs/StudioDialog/StudioDialog';
import { RightStudioDrawer } from '../../components/RightDrawer/RightStudioDrawer';
import { PlusIcon, SearchIcon, } from '../../Icons';
import { showServicesDialog, showStudioDialog, } from '../../store/reducers/dialogSlice';
import { closeDrawer, openDrawer } from '../../store/reducers/drawerSlice';
import { resetSelectedRow, setSelectedRow } from '../../store/reducers/tableSlice';
import { SortTypes as ServicesSortType } from '../services/ServicesScreen.types';

import { SelectedStudioHeader } from './components/SelectedStudioHeader';
import { StudioSortBySelector } from './components/StudioSortBySelector';
import { columns, paramsCatalogue } from './StudiosScreen.data';

export function StudiosScreen () {
  const dispatch = useAppDispatch();

  const {
    isStudioDialogOpen,
    isServicesDialogOpen,
  } = useAppSelector((state: { dialog: any; }) => state.dialog);
  const {
    studios,
    searchParams,
  } = useAppSelector((state) => state.studios);
  const {
    isStudiosLoading,
    isFilteredStudiosLoading,
    selectedToggleBoxValue: toogleBoxValue,
    searchInputValue: searchValue,
    sortByValue: sortValue,
  } = useAppSelector((state) => state.studios);

  const { user } = useAppSelector((state: any) => state.auth);
  // @ts-ignore
  const role = UserRoles[user?.role];

  const compareRoles = (current: string, required: string) => {
    return current === required
  }

  const {
    studio: currentStudio,
  }: any = useAppSelector((state) => state.selectedStudio);

  const selectedStudio = useMemo(() => {
    return studios.find(neededStudio => currentStudio?.id === neededStudio?.id)
  }, [currentStudio, studios]);

  const isSearching = useMemo(() => {
    return searchParams.address || searchParams.title;
  }, [searchParams]);

  const [selectedToggleBox, setSelectedToggleBox] =
    useState<StudiosSearchTypes>(toogleBoxValue);
  const [searchInput, setSearchInput] = useState(searchValue);
  const [sortBy, setSortBy] = useState<string>(sortValue);
  const [isSortSelectOpen, setIsSortSelectOpen] = useState<boolean>(false);

  // SELECTED STUDIO TABLE SECTION
  const [selectedStudioSearchInput, setSelectedStudioSearchInput] = useState('');
  const [selectedStudioToggleBox, setSelectedStudioToggleBox] =
    useState<ServicesSearchTypes>(ServicesSearchTypes.SERVICE);
  const [selectedStudioSortBy, setSelectedStudioSortBy] = useState<string>(ServicesSortType.NAME);
  const [filteredStudioCategories, setFilteredStudioCategories] = useState<any>(null)

  const handleChangeSelectedStudioToggleBox = (nextSelected: ServicesSearchTypes) => {
    setSelectedStudioToggleBox(nextSelected);
  }

  useEffect(() => {
    if (selectedStudio && selectedStudio?.categories) {
      const sortedCategories = sortCategoriesData(selectedStudio?.categories, selectedStudioSortBy);
      setFilteredStudioCategories(sortedCategories);
    }
  }, [selectedStudio, selectedStudioSortBy]);

  const handleChangeSelectedStudioSortBy = (event: any) => {
    setSelectedStudioSortBy(event.target.value as string);
    const sortedData = sortCategoriesData(
      filteredStudioCategories,
      event.target.value
    )
    setFilteredStudioCategories(sortedData)
  }

  const handleChangeSelectedStudioSearchClick = () => {
    const filteredData = sortCategoriesData(
      filterCategoriesData(selectedStudioToggleBox, selectedStudioSearchInput, selectedStudio),
      selectedStudioSortBy
    )
    setFilteredStudioCategories(sortCategoriesData(filteredData, selectedStudioSortBy));
  }
  // END OF SELECTED STUDIO TABLE SECTION

  const handleChange = (nextSelected: StudiosSearchTypes) => {
    setSelectedToggleBox(nextSelected);

    dispatch(updateSearchParams(getSearchParams(paramsCatalogue, sortBy, nextSelected, searchInput)));
    dispatch(getStudios());
  };

  const handleChangeSortBy = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as string);
    dispatch(
      updateSearchParams(getSearchParams(
        paramsCatalogue,
        event.target.value,
        selectedToggleBox,
        searchInput
      )));
    dispatch(getStudios());
  };

  const handleOpenStudioDialog = () => {
    dispatch(showStudioDialog());
  };

  const handleSearchClick = () => {
    dispatch(updateSearchParams(
      getSearchParams(
        paramsCatalogue,
        sortBy,
        selectedToggleBox,
        searchInput
      )
    ));
    dispatch(getStudios());
  };

  const onRowClick = (params: any) => {
    dispatch(setSelectedStudio(params.row))
    dispatch(setSelectedRow(params.row));
    dispatch(openDrawer());
  };

  const handleResetSelectedStudio = () => {
    dispatch(closeDrawer());
    dispatch(resetSelectedRow());
    dispatch(resetSelectedStudio())
  }

  useEffect(() => {
    dispatch(getStudios());
    return () => {
      handleResetSelectedStudio();
    };
  }, []);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [actionType, setActionType] = useState(
    CustomActionCardMenuActionType.EditCategory
  );

  const handleOpenCreateCategoryModal = () => {
    setActionType(CustomActionCardMenuActionType.CreateCategory);
    setIsModalOpen(true);
  };

  const handleOpenServicesDialog = () => {
    dispatch(showServicesDialog());
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        studioId={selectedStudio ? selectedStudio.id : undefined}
        typeOfServiceId={actionType}
        onSubmit={() => {
          setIsModalOpen(false);
          dispatch(getStudios());
        }}
        onClose={() => setIsModalOpen(false)}
      />
      <MainPage
        title="Studios"
        shouldShowHeader={!studios.length && !isStudiosLoading && !isSearching}
        addButtonText='Add studio'
        isDialogOpen={selectedStudio ? isServicesDialogOpen : isStudioDialogOpen}
        isShouldShowEmptyState={!studios.length && !isStudiosLoading && !isSearching}
        onAddButtonClick={handleOpenStudioDialog}
        DialogComp={selectedStudio ?
          <ServicesDialog isOpen={isServicesDialogOpen} /> : <StudioDialog isOpen={isStudioDialogOpen} />
        }
        DrawerComp={selectedStudio ? <RightServicesDrawer /> : <RightStudioDrawer />}
        EmptyStateComp={
          <EmptyPageState
            image="/images/empty-page-states/studios.png"
            title="It seems there are no studios configured in the system."
            subTitle="To start managing studios, add new studio and assign them various roles and responsibilities."
            buttonText='Add studio'
            onButtonClick={handleOpenStudioDialog}
          />
        }
      >
        <>
          <Box
            display="flex"
            flexDirection="row"
            flex={1}
            justifyContent="space-between"
            alignItems="center"
          >
            {
              selectedStudio ? (
                <SelectedStudioHeader
                  studio={selectedStudio}
                  onBack={handleResetSelectedStudio}
                />
              ) : (
                <Typography component="h1" variant="h1">
                  {'Studios'}
                </Typography>
              )
            }

            <Box>
              {
                selectedStudio ? (
                  <>
                    <SubmitButton
                      title={'Add category'}
                      sx={{
                        marginLeft: '16px',
                      }}
                      variant='outlined'
                      endIcon={<PlusIcon />}
                      onClick={handleOpenCreateCategoryModal}
                    />
                    <SubmitButton
                      title={'Add service'}
                      sx={{
                        marginLeft: '16px',
                      }}
                      endIcon={<PlusIcon />}
                      onClick={handleOpenServicesDialog}
                    />
                  </>
                ) : (
                  compareRoles(role, UserRoles.SUPER_ADMIN) ?
                    <SubmitButton
                      title={'Add studio'}
                      endIcon={<PlusIcon />}
                      onClick={handleOpenStudioDialog}
                    />
                    : null
                )
              }
            </Box>
          </Box>
        </>
        <main className="main-page">
          <Box
            display="flex"
            style={{
              padding: '24px 16px 0',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  border: '1px solid #D6D6DB',
                  borderBottomLeftRadius: '8px',
                  borderTopLeftRadius: '8px',
                  borderRight: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  paddingX: 2,
                  paddingY: 1,
                  color: '#1F1F1F',
                  fontWeight: '500',
                }}
              >
                {
                  selectedStudio ? (
                    <>
                      <ToggleBox
                        label={ServicesSearchTypes.SERVICE}
                        onSelected={handleChangeSelectedStudioToggleBox}
                        selected={selectedStudioToggleBox}
                      />
                      <ToggleBox
                        label={ServicesSearchTypes.CATEGORY}
                        onSelected={handleChangeSelectedStudioToggleBox}
                        selected={selectedStudioToggleBox}
                      />
                      <ToggleBox
                        label={ServicesSearchTypes.SUBCATEGORY}
                        onSelected={handleChangeSelectedStudioToggleBox}
                        selected={selectedStudioToggleBox}
                      />
                    </>
                  ) : (
                    <>
                      <ToggleBox
                        label={StudiosSearchTypes.NAME}
                        onSelected={handleChange}
                        selected={selectedToggleBox}
                      />
                      <ToggleBox
                        label={StudiosSearchTypes.ADDRESS}
                        onSelected={handleChange}
                        selected={selectedToggleBox}
                      />
                    </>
                  )
                }
              </Box>
              <Box
                display="flex"
                sx={{
                  border: '1px solid #D6D6DB',
                  backgroundColor: '#FAFAFA',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                  flex: 1,
                  maxWidth: '432px',
                }}
              >
                <InputBase
                  sx={{ ml: 2, flex: 1, fontSize: '14px' }}
                  placeholder="Search"
                  value={!selectedStudio ? searchInput : selectedStudioSearchInput}
                  onChange={(e) =>
                    !selectedStudio ? setSearchInput(e.target.value) : setSelectedStudioSearchInput(e.target.value)
                  }
                />
                <IconButton
                  type="button"
                  onClick={!selectedStudio ? handleSearchClick : handleChangeSelectedStudioSearchClick}
                >
                  <SearchIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ minWidth: 120, marginLeft: "16px"  }}>
              <FormControl fullWidth>
                <InputLabel id="select-sort-by-label">Sort by</InputLabel>
                <StudioSortBySelector
                  selectedStudio={selectedStudio}
                  value={selectedStudio ? selectedStudioSortBy : sortBy}
                  onOpen={() => setIsSortSelectOpen(true)}
                  onClose={() => setIsSortSelectOpen(false)}
                  onChange={selectedStudio ? handleChangeSelectedStudioSortBy : handleChangeSortBy}
                  isOpen={isSortSelectOpen}
                />
              </FormControl>
            </Box>
          </Box>

          <Box
            style={{
              padding: '32px 16px',
              paddingBottom: studios.length === 0 ? '72px' : undefined,
            }}
          >
            {
              selectedStudio ? (
                <StudioCategoriesTable
                  studioCategories={
                    filteredStudioCategories
                  }
                />
              ) : (
                <Table
                  rows={studios}
                  columns={columns}
                  loading={isStudiosLoading || isFilteredStudiosLoading}
                  onRowClick={onRowClick}
                />
              )
            }
          </Box>
        </main>
      </MainPage>
    </>
  );
}
